import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import ProductCard from './ProductCard';
import SizeModal from './SizeModal';
import SkeletonCard from './skeletons/SkeletonCard';

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortOption, setSortOption] = useState('');
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [priceFilter, setPriceFilter] = useState('');
  const [showFilters, setShowFilters] = useState(window.innerWidth >= 768);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromURL = params.get('category');
    const searchFromURL = params.get('search');

    if (categoryFromURL) {
      setSelectedProductTypes([categoryFromURL.toLowerCase()]);
    }

    if (searchFromURL) {
      setSearch(searchFromURL);
    }

    window.scrollTo(0, 0); // Optional: Scroll to top on param change
  }, [location.search]);

  const { cart, addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/products/all')
      .then(res => {
        setProducts(res.data);
        setFiltered(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let filteredProducts = [...products];

    if (search) {
      const terms = search.toLowerCase().split(' '); // split by words
      filteredProducts = filteredProducts.filter((p) => {
        const searchable = [
          p.name,
          ...(p.productType || []),
          p.category,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
    
        return terms.every(term => searchable.includes(term));
      });
    }
    
    // Existing filters for product types, price range, and sorting
    if (selectedProductTypes.length > 0) {
      filteredProducts = filteredProducts.filter(p =>
        p.productType && selectedProductTypes.some(type =>
          p.productType.map(pt => pt.toLowerCase()).includes(type.toLowerCase())
        )
      );
    }

    if (priceFilter === 'under100') {
      filteredProducts = filteredProducts.filter(p => p.price < 100);
    } else if (priceFilter === 'under200') {
      filteredProducts = filteredProducts.filter(p => p.price < 200);
    } else if (priceFilter === 'under300') {
      filteredProducts = filteredProducts.filter(p => p.price < 300);
    } else if (priceFilter === 'under400') {
      filteredProducts = filteredProducts.filter(p => p.price < 400);
    } else if (priceFilter === 'over400') {
      filteredProducts = filteredProducts.filter(p => p.price > 400);
    }

    if (sortOption === 'highest') {
      filteredProducts.sort((a, b) => b.price - a.price);
    } else if (sortOption === 'lowest') {
      filteredProducts.sort((a, b) => a.price - b.price);
    } else if (sortOption === 'new') {
      filteredProducts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortOption === 'bestsellers') {
      filteredProducts.sort((a, b) => b.price - a.price); // placeholder logic
    }

    setFiltered(filteredProducts);
  }, [search, selectedProductTypes, priceFilter, sortOption, products]);

  const handleProductTypeChange = (type) => {
    setSelectedProductTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const clearFilters = () => {
    setSearch('');
    setSortOption('');
    setSelectedProductTypes([]);
    setPriceFilter('');
  };

  const openSizeModal = (product) => {
    setSelectedProduct(product);
    setSelectedSize('');
    setModalOpen(true);
  };

  const handleConfirmAdd = () => {
  const sizeToUse = selectedProduct.productType.includes("Earring")
    ? "Default"
    : selectedSize;

  if (!sizeToUse) {
    toast.error('Please select a size.');
    return;
  }

  const selectedSizeObject = selectedProduct.sizes.find(s => s.size === sizeToUse);

  if (!selectedSizeObject || selectedSizeObject.stock <= 0) {
    toast.error('Selected size is out of stock.');
    return;
  }

  addToCart({ ...selectedProduct, size: sizeToUse });
  setModalOpen(false);
};


  const hasActiveFilters = search || sortOption || selectedProductTypes.length > 0 || priceFilter;

  return (
    <div className="p-4 max-w-7xl pt-20 mx-auto">
      <div className="mb-4">
        <h2 className="text-3xl font-bold mb-2">Jewelry Shop</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(prev => !prev)}
            className="text-blue-600 border px-3 md:py-1 rounded hover:bg-blue-100"
          >
            <span className="md:hidden text-sm">
              {showFilters ? '❮ Hide Filters' : '❯ Show Filters'}
            </span>
            <span className="hidden md:inline">Toggle Filters</span>
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="bg-gray-300 hover:bg-gray-400 text-sm px-3 py-1 rounded"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row transition-all duration-500 ease-in-out">
        {/* Filter Sidebar */}
        <div
          className={`transition-all duration-500 ease-in-out
    ${showFilters ? 'block md:block md:w-1/4 p-4 border-r' : 'hidden md:w-0'}
  `}
        >



          <input
            type="text"
            placeholder="Search..."
            className="border p-2 rounded w-full mb-4"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <hr className="my-4" />

          <div className="mb-4">
            <h3 className="font-semibold mb-2">Sort Selection</h3>
            <select
              className="w-full border p-2 rounded"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="">Default</option>
              <option value="highest">Highest Price</option>
              <option value="lowest">Lowest Price</option>
              <option value="new">New Arrivals</option>
              <option value="bestsellers">Best Sellers</option>
            </select>
          </div>

          <hr className="my-4" />

          <div className="mb-4">
            <h3 className="font-semibold mb-2">Product Type</h3>
            {['necklace', 'bracelet', 'ring', 'earring'].map(type => (
              <label key={type} className="block">
                <input
                  type="checkbox"
                  name="productType"
                  value={type}
                  checked={selectedProductTypes.includes(type)}
                  onChange={() => handleProductTypeChange(type)}
                /> {type.charAt(0).toUpperCase() + type.slice(1)}
              </label>
            ))}
          </div>

          <hr className="my-4" />

          <div className="mb-4">
            <h3 className="font-semibold mb-2">Price Range</h3>
            {['under100', 'under200', 'under300', 'under400', 'over400', ''].map(price => (
              <label key={price} className="block">
                <input
                  type="radio"
                  name="price"
                  value={price}
                  checked={priceFilter === price}
                  onChange={(e) => setPriceFilter(e.target.value)}
                /> {
                  price === 'under100' ? 'Under $100' :
                    price === 'under200' ? 'Under $200' :
                      price === 'under300' ? 'Under $300' :
                        price === 'under400' ? 'Under $400' :
                          price === 'over400' ? 'Over $400' : 'All Prices'
                }
              </label>
            ))}
          </div>
        </div>

        {/* Product Grid */}
        <div className="w-full p-0 md-p-4">
        {loading ? (
  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
    {Array.from({ length: 8 }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
) : (
  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
    {filtered.map(product => {
      const inCart = cart.some(item => item._id === product._id);
      return (
        <ProductCard
          key={product._id}
          product={product}
          inCart={inCart}
                    onAddToCart={(product) => {
                      const outOfStock = product.sizes.reduce((sum, s) => sum + s.stock, 0) === 0;
                      if (outOfStock) return;

                      const isEarring = product.productType?.includes('Earring');
                      const hasOnlyDefaultSize = product.sizes.length === 1 && product.sizes[0].size === 'Default';

                      if (isEarring && hasOnlyDefaultSize) {
                        addToCart({ ...product, size: 'Default' });
                      } else {
                        const firstAvailable = product.sizes.find(s => s.stock > 0);
                        if (!firstAvailable) return toast.error('All sizes out of stock');

                        setSelectedProduct(product);
                        setSelectedSize(firstAvailable.size);
                        setModalOpen(true);
                      }
                    }}
                  />
                );
              })}

            </div>)}
        </div>
      </div>

      {modalOpen && selectedProduct && (
        <SizeModal
          product={selectedProduct}
          selectedSize={selectedSize}
          setSelectedSize={setSelectedSize}
          onCancel={() => setModalOpen(false)}
          onConfirm={handleConfirmAdd}
        />
      )}
    </div>
  );
};

export default Shop;
